import { createContext, useContext, useState, useEffect } from 'react'
import { loadDBFromSupabase } from '@/lib/supabase'

const DB_KEY = 'novacms_db'

// ─── Default content for every editable field ─────────────────────────────────
export const DEFAULT_CONTENT: Record<string, Record<string, string>> = {
  home: {
    // Hero
    hero_badge:      'Premium kućni brojevi po meri',
    hero_title:      'KUĆNI BROJEVI — ELEGANCIJA I PREPOZNATLJIVOST ZA VAŠ DOM',
    hero_desc:       'Naši personalizovani kućni brojevi od klirita i kliritnog ogledala pružaju moderan dizajn i vrhunski kvalitet, savršeni za istaknute adrese vašeg doma.',
    hero_cta1:       'Vidi proizvode',
    hero_cta2:       'Saznaj više',
    hero_reviews:    '4.9 od 3.847+ recenzija',
    hero_delivery:   'Dostava 2-4 dana',
    hero_warranty:   '5 godina garancije',
    // About / Stats
    about_heading:   'POSTANI DEO NAŠE PRIČE',
    about_text:      'Sa preko 50.000 izrađenih kućnih brojeva i više od 30.000 zadovoljnih kupaca, naši proizvodi ukrase domove širom Srbije. Nudimo brzu izradu, brzu isporuku i moderan dizajn.',
    stat1_val:       '50.000+',
    stat1_label:     'Proizvedeno proizvoda',
    stat2_val:       '35.000+',
    stat2_label:     'Zadovoljnih kupaca',
    stat3_val:       '4.9',
    stat3_label:     'Prosečna ocena',
    stat4_val:       '2-4',
    stat4_label:     'Dana isporuke',
    // Features
    features_heading: 'NAPRAVLJENI DA ZADIVE SVAKOGA',
    features_text:    'Privucite pogled svojim stilom! Naša kolekcija brojeva, slova, pločica sa prezimenom, oznaka "čuvaj se psa," nalepnica za poštansko sanduče i tablona za moderne kućne brojeve pažljivo je osmišljena da istakne lepotu vašeg doma.',
    features_cta:     'Svi proizvodi',
    // Products section
    products_heading:  'NAŠI PROIZVODI',
    products_subtitle: 'Izaberite iz naše široke ponude personalizovanih kućnih brojeva, pločica i oznaka.',
    products_cta:      'Vidi sve proizvode',
    // Reviews
    reviews_heading:  'ŠTA KAŽU NAŠI KUPCI?',
    // FAQ
    faq_heading:   'Najčešća pitanja',
    faq_subtitle:  'Sve što treba da znate pre poručivanja kućnih brojeva, pločica i oznaka.',
    faq1_q: 'Koliko koštaju kućni brojevi po meri?',
    faq1_a: 'Cene kućnih brojeva po meri kreću se od 1.500 RSD, u zavisnosti od materijala, veličine i složenosti dizajna. Pločice za vrata su od 1.700 RSD, a oznake za poštansko sanduče od 1.500 RSD.',
    faq2_q: 'Koliko traje izrada i isporuka kućnih brojeva?',
    faq2_a: 'Izrada kućnih brojeva traje 1 radni dan. Isporuka putem brze pošte je 2-4 radna dana širom Srbije. Za Beograd isporuka može biti i brža.',
    faq3_q: 'Iz kog materijala su kućni brojevi?',
    faq3_a: 'Naši kućni brojevi su izrađeni od klirita (akrilnog stakla) i kliritnog ogledala. Materijal je otporan na vremenske uslove, UV zrake i ne bledi na suncu.',
    faq4_q: 'Da li kućni brojevi dolaze sa setom za montažu?',
    faq4_a: 'Da, svaki kućni broj dolazi sa kompletnim setom za montažu koji uključuje distancere, šrafove i tiplove. Uputstvo za postavljanje je priloženo u paketu.',
    faq5_q: 'Da li mogu da vidim dizajn pre poručivanja?',
    faq5_a: 'Da, pre konačne potvrde porudžbine dostavljamo vam vizuelni prikaz odabranog proizvoda. Možete zatražiti izmene pre nego što proizvod uđe u izradu.',
    faq6_q: 'Da li vršite isporuku širom Srbije?',
    faq6_a: 'Da, isporučujemo širom Srbije putem brze pošte. Dostava je besplatna za porudžbine preko 5.000 RSD. Za Beograd i Novi Sad nudimo i ekspres dostavu.',
    // Newsletter
    newsletter_heading:  'Budite u toku sa novostima',
    newsletter_subtitle: 'Prijavite se na naš newsletter i budite prvi koji saznaju za popuste i nove proizvode.',
    newsletter_placeholder: 'Vaša email adresa',
    newsletter_cta:      'Prijavi se',
  },
  products: {
    page_title:    'Naši Proizvodi',
    page_subtitle: 'Pronađite savršen kućni broj ili pločicu za Vaš dom',
  },
  about: {
    page_title:     'O Nama',
    intro_text:     'Specijalizovani smo za izradu personalizovanih kućnih brojeva i pločica od premium materijala.',
    mission_title:  'Naša Misija',
    mission_text:   'Svaki kućni broj koji izradimo nosi pečat kvaliteta i pažnje prema detaljima. Cilj nam je da svaki dom dobije jedinstven i elegantan identitet.',
    address:        'Beograd, Srbija',
    phone:          '+381 61 292 9053',
    email:          'info@kucnibrojevi.com',
  },
  contact: {
    page_title: 'Kontakt',
    address:    'Beograd, Srbija',
    phone:      '+381 61 292 9053',
    email:      'info@kucnibrojevi.com',
    hours:      'Pon–Pet: 08:00–17:00',
    map_url:    '',
  },
  blog: {
    page_title:    'Blog',
    page_subtitle: 'Saveti, inspiracija i novosti iz sveta kućnih brojeva',
  },
}

// ─── Types ────────────────────────────────────────────────────────────────────
type ContentDB = {
  pages?: Record<string, { content?: Record<string, string>; seo?: Record<string, string> }>
  products?: unknown[]
  blog?: unknown[]
}

type ContentContextValue = {
  pages: Record<string, Record<string, string>>
}

const ContentContext = createContext<ContentContextValue>({ pages: DEFAULT_CONTENT })

// ─── Provider ────────────────────────────────────────────────────────────────
export function ContentProvider({ children }: { children: React.ReactNode }) {
  const [pages, setPages] = useState<Record<string, Record<string, string>>>(() => {
    // Hydrate from localStorage immediately (same key as CMS)
    try {
      const s = localStorage.getItem(DB_KEY)
      if (s) {
        const db = JSON.parse(s) as ContentDB
        return mergePages(db.pages)
      }
    } catch { /* ignore */ }
    return DEFAULT_CONTENT
  })

  useEffect(() => {
    // Load from Supabase on mount (may update after initial render)
    loadDBFromSupabase().then(remote => {
      if (remote) {
        const db = remote as ContentDB
        setPages(mergePages(db.pages))
      }
    })

    // React to CMS saves in same tab (CMS writes to localStorage then dispatches this event)
    const onStorage = (e: StorageEvent) => {
      if (e.key === DB_KEY && e.newValue) {
        try {
          const db = JSON.parse(e.newValue) as ContentDB
          setPages(mergePages(db.pages))
        } catch { /* ignore */ }
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return (
    <ContentContext.Provider value={{ pages }}>
      {children}
    </ContentContext.Provider>
  )
}

// Merge CMS pages with defaults so missing keys always fall back to defaults
function mergePages(
  cmsPages?: Record<string, { content?: Record<string, string> }> | null
): Record<string, Record<string, string>> {
  if (!cmsPages) return DEFAULT_CONTENT
  const result: Record<string, Record<string, string>> = {}
  for (const slug of Object.keys(DEFAULT_CONTENT)) {
    result[slug] = {
      ...DEFAULT_CONTENT[slug],
      ...(cmsPages[slug]?.content ?? {}),
    }
  }
  return result
}

// ─── Hooks ───────────────────────────────────────────────────────────────────
export const useContent = () => useContext(ContentContext)

/** Returns editable content for a single page slug, merged with defaults */
export const usePageContent = (slug: string): Record<string, string> => {
  const { pages } = useContext(ContentContext)
  return pages[slug] ?? DEFAULT_CONTENT[slug] ?? {}
}
